/**
 * Standing-latency drain: the host asks the guest to act on its receiver, the guest reports
 * what happened.
 *
 * Hitch-free by contract. The only actions are receiver hints: re-assert the low-latency
 * target, or pulse it (a small non-zero target, then back) to nudge an engine whose timing
 * module ignored an unchanged write. Nothing here may reconnect, replace a track, restart
 * ICE or reset a decoder; a lever that hitches is not a drain, it is a different failure.
 *
 * Rides the existing datachannel beside `clientStats` and `inputPhoton`. Both message types
 * are unknown-safe: an older peer's router drops them.
 */

import { isReceiverHintReadback, type ReceiverHintReadback } from '../receiverHints/receiverHintContracts';

export const STANDING_LATENCY_DRAIN_MESSAGE_TYPE = 'standingLatencyDrain' as const;
export const STANDING_LATENCY_DRAIN_RESULT_MESSAGE_TYPE = 'standingLatencyDrainResult' as const;

export type DrainAction = 'reassert' | 'pulse';
const DRAIN_ACTIONS: ReadonlyArray<DrainAction> = ['reassert', 'pulse'];

/** How long the host waits after a step before judging it. */
export const DRAIN_VERIFY_MS = 3_000;
/** Total steps the host will take for one standing episode before declaring it exhausted. */
export const DRAIN_MAX_ATTEMPTS = 3;
/** The non-zero target a `pulse` writes before returning to the low-latency target. */
export const DRAIN_PULSE_TARGET_MS = 50;

export type StandingLatencyDrainRequest = {
  type: typeof STANDING_LATENCY_DRAIN_MESSAGE_TYPE;
  /** Host-assigned, monotonic per peer; echoed on the result. */
  seq: number;
  action: DrainAction;
  /** Only for `pulse`; defaults to DRAIN_PULSE_TARGET_MS on the guest when absent. */
  pulseTargetMs?: number;
};

export type StandingLatencyDrainResult = {
  type: typeof STANDING_LATENCY_DRAIN_RESULT_MESSAGE_TYPE;
  seq: number;
  action: DrainAction;
  /** False when no receiver was attached or the write threw; `error` says why. */
  applied: boolean;
  readback: ReceiverHintReadback;
  error?: string;
};

function isDrainAction(value: unknown): value is DrainAction {
  return typeof value === 'string' && (DRAIN_ACTIONS as ReadonlyArray<string>).includes(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export function isStandingLatencyDrainRequest(value: unknown): value is StandingLatencyDrainRequest {
  if (value === null || typeof value !== 'object') return false;
  const m = value as Record<string, unknown>;
  if (m['type'] !== STANDING_LATENCY_DRAIN_MESSAGE_TYPE) return false;
  if (!isFiniteNumber(m['seq'])) return false;
  if (!isDrainAction(m['action'])) return false;
  if (m['pulseTargetMs'] !== undefined && !isFiniteNumber(m['pulseTargetMs'])) return false;
  return true;
}

export function isStandingLatencyDrainResult(value: unknown): value is StandingLatencyDrainResult {
  if (value === null || typeof value !== 'object') return false;
  const m = value as Record<string, unknown>;
  if (m['type'] !== STANDING_LATENCY_DRAIN_RESULT_MESSAGE_TYPE) return false;
  if (!isFiniteNumber(m['seq'])) return false;
  if (!isDrainAction(m['action'])) return false;
  if (typeof m['applied'] !== 'boolean') return false;
  if (!isReceiverHintReadback(m['readback'])) return false;
  if (m['error'] !== undefined && typeof m['error'] !== 'string') return false;
  return true;
}
